import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isValidIBAN } from 'ibantools-germany';

@Injectable()
export class ValidationsService {
  public static lettersAndNumbersPattern = '^([a-zA-Z0-9äöüÄÖÜß])+$';
  public static lettersNumbersSpacesPattern = '^([a-zA-Z0-9äöüÄÖÜß ])+$';

  static emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const emailRegex = new RegExp('^[\\wäöüÄÖÜß-]+(?:\\.[\\wäöüÄÖÜß-]+)*@[a-zA-Z0-9äöüÄÖÜß-]+(?:\\.[a-zA-Z0-9äöüÄÖÜß-]+)+$');
      if (!emailRegex.test(control.value)) {
        return { emailError: true };
      }
      return null;
    };
  }

  static maxLengthValidator(maxLength: number = 5): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let controlValue = (control.value && control.value.toString()) || '';
      let returnObject = null;
      if (!controlValue) return returnObject;

      const maxLengthRegex = new RegExp(`^[0-9]{${maxLength}}$`);

      if (controlValue.length && controlValue.length > maxLength) {
        controlValue = controlValue.slice(0, maxLength);
        control.setValue(controlValue);
      }
      if (!maxLengthRegex.test(controlValue)) {
        returnObject = { maxLengthError: true };
      }
      return returnObject;
    };
  }

  static stringComparisonValidator(fieldOne: string, fieldTwo: string): ValidatorFn {
    return (formGroup: FormGroup) => {
      const controlOne = formGroup.get(fieldOne);
      const controlTwo = formGroup.get(fieldTwo);
      let returnObject = null;
      if (!controlOne || !controlTwo) return returnObject;

      const controlValue = controlOne.value || '';
      const comparisonValue = controlTwo.value || '';

      if (controlValue !== comparisonValue) {
        returnObject = { unequalStringError: true };
        controlOne.setErrors({
          ...controlOne.errors,
          ...{ unequalStringError: true },
        });
        controlTwo.setErrors({
          ...controlTwo.errors,
          ...{ unequalStringError: true },
        });
      } else {
        let controlOneError = { ...controlOne.errors };
        delete controlOneError.unequalStringError;
        controlOneError = Object.keys(controlOneError).length > 0 ? controlOneError : null;
        controlOne.setErrors(controlOneError);

        let controlTwoError = { ...controlTwo.errors };
        delete controlTwoError.unequalStringError;
        controlTwoError = Object.keys(controlTwoError).length > 0 ? controlTwoError : null;
        controlTwo.setErrors(controlTwoError);
      }
      return returnObject;
    };
  }

  static streetNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value || '';
      let returnObject = null;
      if (!controlValue) return returnObject;

      const streetNumberRegex = new RegExp('^[0-9a-zA-Z]+(/[0-9a-zA-Z]+)?$');

      if (!streetNumberRegex.test(controlValue)) {
        returnObject = { streetNumberError: true };
      }
      return returnObject;
    };
  }

  static isFieldRequired(formControl: AbstractControl) {
    if (!formControl.validator) {
      return false;
    }
    const validator = formControl.validator({} as AbstractControl);
    return validator && validator.required;
  }

  static feedInPermissionValidDateShouldBeInTheFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const feedInPermissionValidDate = new Date(control.value);
      const today = new Date();

      if (feedInPermissionValidDate > today) {
        return null;
      }

      return {
        feedInPermissionValidDateShouldBeInTheFutureValidator: true,
      };
    };
  }

  static ibanValidator(onlyGerman: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const value = control.value.replace(/\s+/g, '');
      if (!isValidIBAN(value, onlyGerman)) {
        return { ibanError: true };
      }

      return null;
    };
  }

  static zerezIdValidator(): ValidatorFn {
    const zerezIdPattern = /^ZE-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    return Validators.pattern(zerezIdPattern);
  }

  static lettersAndNumbersValidator(): ValidatorFn {
    return Validators.pattern(ValidationsService.lettersAndNumbersPattern);
  }

  static lettersNumbersSpacesValidator(): ValidatorFn {
    return Validators.pattern(ValidationsService.lettersNumbersSpacesPattern);
  }
}
